import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';

import AppsIcon from '@atlaskit/icon/core/apps';

import GlobeIcon from '@atlaskit/icon/core/globe';

import HomeIcon from '@atlaskit/icon/core/home';

import PageIcon from '@atlaskit/icon/core/page';

import TasksIcon from '@atlaskit/icon/core/tasks';

import TeamsIcon from '@atlaskit/icon/core/teams';

import VideoIcon from '@atlaskit/icon/core/video';

import { type useIntl } from 'react-intl';

import {
	buttonAndAltMessages,
	featureMessages,
	iconLabelMessages,
	modalDescriptionMessages,
	modalTitleMessages,
} from './messages';

import {
	type Content,
	type ContentConfig,
	type DismissButton,
	type LinkButton,
	type TakeTourButton,
	type Variant,
} from '../types';

const getTakeTourButton = (variant: Exclude<Variant, 'none'>) => {
	return {
		action: 'takeTour',
		actionSubject: 'modalCTA',
		message: buttonAndAltMessages.takeATourButton,
		appearance: 'primary' as const,
		id: 'take-tour-button',
		testId: 'take-tour-button',
		analyticAttributes: {
			action: 'takeTour',
			id: 'take-tour-button',
			variant,
		},
	} as TakeTourButton;
};

export const getGotItButton = (variant: Exclude<Variant, 'none'>): DismissButton => {
	return {
		action: 'dismiss',
		actionSubject: 'modalCTA',
		message: buttonAndAltMessages.gotItButton,
		appearance: 'primary' as const,
		id: 'got-it-button',
		testId: 'got-it-button',
		analyticAttributes: {
			action: 'dismiss',
			id: 'got-it-button',
			variant,
		},
	} as DismissButton;
};

const getExploreAtlassianHomeButton = (variant: Exclude<Variant, 'none'>) => {
	return {
		action: 'dismiss',
		actionSubject: 'dismissModalButton',
		appearance: 'subtle' as const,
		message: buttonAndAltMessages.exploreAtlassianHome,
		id: 'explore-atlassian-home',
		testId: 'explore-atlassian-home',
		analyticAttributes: {
			action: 'dismiss',
			id: 'explore-atlassian-home',
			variant,
		},
	} as DismissButton;
};

const getExploreLoomButton = (variant: Exclude<Variant, 'none'>) => {
	return {
		action: 'link',
		actionSubject: 'modalCTA',
		message: buttonAndAltMessages.exploreLoom,
		appearance: 'primary' as const,
		href: 'https://loom.com/api/auth/atlassian',
		id: 'explore-loom',
		testId: 'explore-loom',
		analyticAttributes: {
			action: 'link',
			id: 'explore-loom',
			variant,
		},
	} as LinkButton;
};

type ContentConfigOptions = {
	showSpotlightTour: boolean;
	appOneName?: string;
	appTwoName?: string;
};

export const getContentConfig = (
	variant: Exclude<Variant, 'none'>,
	formatMessage: ReturnType<typeof useIntl>['formatMessage'],
	options: ContentConfigOptions,
): Content => {
	const { showSpotlightTour, appOneName, appTwoName } = options;

	const config: ContentConfig = {
		twc: {
			title: modalTitleMessages.pimary,
			description: modalDescriptionMessages.primary,
			features: [
				{
					icon: (
						<TasksIcon
							label={formatMessage(iconLabelMessages.jiraIconLabel)}
							color="var(--ds-icon-brand)"
							spacing="spacious"
						/>
					),
					message: featureMessages.jiraFeature,
				},
				{
					icon: (
						<PageIcon
							label={formatMessage(iconLabelMessages.confluenceIconLabel)}
							color="var(--ds-icon-brand)"
							spacing="spacious"
						/>
					),
					message: featureMessages.confluenceFeature,
				},
				{
					icon: (
						<VideoIcon
							label={formatMessage(iconLabelMessages.loomIconLabel)}
							color="var(--ds-icon-brand)"
							spacing="spacious"
						/>
					),
					message: featureMessages.loomFeature,
				},
				{
					icon: (
						<GlobeIcon
							label={formatMessage(iconLabelMessages.integrationIconLabel)}
							color="var(--ds-icon-brand)"
							spacing="spacious"
						/>
					),
					message: featureMessages.integrationsBringTogether,
				},
			],
			buttons: showSpotlightTour ? [getTakeTourButton(variant)] : [getGotItButton(variant)],
		},
		loom: {
			title: modalTitleMessages.loom,
			description: modalDescriptionMessages.loom,
			features: [
				{
					icon: (
						<TasksIcon
							label={formatMessage(iconLabelMessages.jiraIconLabel)}
							color="var(--ds-icon-accent-blue)"
							spacing="spacious"
						/>
					),
					message: featureMessages.loomAddDemos,
				},
				{
					icon: (
						<PageIcon
							label={formatMessage(iconLabelMessages.confluenceIconLabel)}
							color="var(--ds-icon-accent-blue)"
							spacing="spacious"
						/>
					),
					message: featureMessages.loomUseVideos,
				},
				{
					icon: (
						<GlobeIcon
							label={formatMessage(iconLabelMessages.integrationIconLabel)}
							color="var(--ds-icon-accent-blue)"
							spacing="spacious"
						/>
					),
					message: featureMessages.integrationsBringTogether,
				},
			],
			buttons: showSpotlightTour
				? [getTakeTourButton(variant)]
				: [getExploreAtlassianHomeButton(variant), getExploreLoomButton(variant)],
		},
		var3: {
			title: modalTitleMessages.newApps,
			description: modalDescriptionMessages.primary,
			features: [
				{
					icon: (
						<AlignTextLeftIcon
							label={formatMessage(iconLabelMessages.alignTextLeftLabel)}
							color="var(--ds-icon-brand)"
							spacing="spacious"
						/>
					),
					message: featureMessages.jiraConfluenceFeatures,
				},
				{
					icon: (
						<VideoIcon
							label={formatMessage(iconLabelMessages.loomIconLabel)}
							color="var(--ds-icon-brand)"
							spacing="spacious"
						/>
					),
					message: featureMessages.loomShareUpdates,
				},
				{
					icon: (
						<GlobeIcon
							label={formatMessage(iconLabelMessages.integrationIconLabel)}
							color="var(--ds-icon-accent-blue)"
							spacing="spacious"
						/>
					),
					message: featureMessages.connectIntegrations,
				},
			],
			buttons: showSpotlightTour ? [getTakeTourButton(variant)] : [getGotItButton(variant)],
		},
		var4: {
			title: modalTitleMessages.connectWork,
			description: modalDescriptionMessages.primary,
			features: [
				{
					icon: (
						<TasksIcon
							label={formatMessage(iconLabelMessages.jiraIconLabel)}
							color="var(--ds-icon-brand)"
							spacing="spacious"
						/>
					),
					message: featureMessages.jiraFeature,
				},
				{
					icon: (
						<PageIcon
							label={formatMessage(iconLabelMessages.confluenceIconLabel)}
							color="var(--ds-icon-brand)"
							spacing="spacious"
						/>
					),
					message: featureMessages.confluenceFeature,
				},
				{
					icon: (
						<VideoIcon
							label={formatMessage(iconLabelMessages.loomIconLabel)}
							color="var(--ds-icon-brand)"
							spacing="spacious"
						/>
					),
					message: featureMessages.loomFeature,
				},
				{
					icon: (
						<TeamsIcon
							label={formatMessage(iconLabelMessages.teamsIconLabel)}
							color="var(--ds-icon-brand)"
							spacing="spacious"
						/>
					),
					message: featureMessages.integrationsBringTogetherNoGoals,
				},
			],
			buttons: showSpotlightTour ? [getTakeTourButton(variant)] : [getGotItButton(variant)],
		},
		var5: {
			title: modalTitleMessages.loom,
			description: modalDescriptionMessages.loom,
			features: [
				{
					icon: (
						<TasksIcon
							label={formatMessage(iconLabelMessages.jiraIconLabel)}
							color="var(--ds-icon-accent-blue)"
							spacing="spacious"
						/>
					),
					message: featureMessages.loomAddDemos,
				},
				{
					icon: (
						<PageIcon
							label={formatMessage(iconLabelMessages.confluenceIconLabel)}
							color="var(--ds-icon-accent-blue)"
							spacing="spacious"
						/>
					),
					message: featureMessages.loomUseVideos,
				},
				{
					icon: (
						<GlobeIcon
							label={formatMessage(iconLabelMessages.integrationIconLabel)}
							color="var(--ds-icon-accent-blue)"
							spacing="spacious"
						/>
					),
					message: featureMessages.integrationsBringTogetherNoGoals,
				},
			],
			buttons: showSpotlightTour ? [getTakeTourButton(variant)] : [getGotItButton(variant)],
		},
		var6: {
			title: modalTitleMessages.newApps,
			description: modalDescriptionMessages.primary,
			features: [
				{
					icon: (
						<AppsIcon
							label={formatMessage(iconLabelMessages.appsIconLabel)}
							color="var(--ds-icon-accent-blue)"
							spacing="spacious"
						/>
					),
					message:
						appOneName && appTwoName ? featureMessages.newAppsTwo : featureMessages.newAppsOne,
				},
				{
					icon: (
						<HomeIcon
							label={formatMessage(iconLabelMessages.homeIconLabel)}
							color="var(--ds-icon-accent-blue)"
							spacing="spacious"
						/>
					),
					message: featureMessages.atlassianHome,
				},
				{
					icon: (
						<GlobeIcon
							label={formatMessage(iconLabelMessages.integrationIconLabel)}
							color="var(--ds-icon-accent-blue)"
							spacing="spacious"
						/>
					),
					message: featureMessages.integrationsBringTogetherNoGoals,
				},
			],
			buttons: showSpotlightTour ? [getTakeTourButton(variant)] : [getGotItButton(variant)],
		},
	};

	return config[variant];
};
